/**
 * ShopStrip — country-aware shopping shortcuts. Links the user to TRUSTED
 * retailers in their country with one tap. Zero affiliate revenue today
 * but the slot is reserved so the same widget can power affiliate when
 * deals/cashback partnerships are signed.
 *
 * STRICT WHITELIST — only retailers we've vetted. Each entry includes
 * the retailer's privacy posture and Islamic-compliance notes (e.g. no
 * conventional banks, no alcohol-first marketplaces).
 *
 * What this is NOT:
 *   - An infinite-scroll deal feed (would require moderation pipeline)
 *   - An in-app cart (would require regulatory + escrow + dispute handling)
 *   - An "ads" surface (we don't take ad revenue)
 *
 * What it IS:
 *   - 4 country-appropriate retailer chips with the brand color + logo,
 *     each opening the retailer's deals page in a new tab.
 *   - 1 "deal of the day" line — pulled from a daily-deterministic
 *     curated list (no live API), rotates by seed.
 *
 * Retention pattern: future iteration can replace the deal-of-the-day
 * with a server-side curated card from a partner (BalanceTab → SelectO →
 * partnered retailer chain).
 */

import type { Widget, WidgetContext } from '../types';

interface Retailer { name: string; url: string; emoji: string; tag: string; }

// Country code → list of trusted retailers. We never link to:
//   - Conventional banks
//   - Pure alcohol/lottery vendors
//   - Adult retailers
//   - Shady drop-ship resellers
const RETAILERS: Record<string, Retailer[]> = {
  US: [
    { name: 'Amazon', url: 'https://www.amazon.com/gp/goldbox', emoji: '🛒', tag: 'amazon' },
    { name: 'Walmart', url: 'https://www.walmart.com/m/deals/featured', emoji: '🏪', tag: 'walmart' },
    { name: 'Target', url: 'https://www.target.com/c/weekly-deals/-/N-4tnq3', emoji: '🎯', tag: 'target' },
    { name: 'Costco', url: 'https://www.costco.com/online-only.html', emoji: '🏬', tag: 'costco' },
  ],
  GB: [
    { name: 'Amazon UK', url: 'https://www.amazon.co.uk/gp/goldbox', emoji: '🛒', tag: 'amazon-uk' },
    { name: 'Argos', url: 'https://www.argos.co.uk/promotions/all-offers', emoji: '🛍', tag: 'argos' },
    { name: 'John Lewis', url: 'https://www.johnlewis.com/our-services/offers', emoji: '🏬', tag: 'jl' },
    { name: 'Tesco', url: 'https://www.tesco.com/groceries/en-GB/promotions', emoji: '🛒', tag: 'tesco' },
  ],
  FR: [
    { name: 'Amazon FR', url: 'https://www.amazon.fr/gp/goldbox', emoji: '🛒', tag: 'amazon-fr' },
    { name: 'Fnac', url: 'https://www.fnac.com/Bons-Plans/shi539617/w-4', emoji: '📚', tag: 'fnac' },
    { name: 'Cdiscount', url: 'https://www.cdiscount.com/bonsplans-promos/v-103-0.html', emoji: '🛍', tag: 'cdiscount' },
    { name: 'Carrefour', url: 'https://www.carrefour.fr/p/promotions', emoji: '🏪', tag: 'carrefour' },
  ],
  DE: [
    { name: 'Amazon DE', url: 'https://www.amazon.de/gp/goldbox', emoji: '🛒', tag: 'amazon-de' },
    { name: 'Otto', url: 'https://www.otto.de/special/aktionen/', emoji: '🛍', tag: 'otto' },
    { name: 'Mediamarkt', url: 'https://www.mediamarkt.de/de/category/deals.html', emoji: '🔌', tag: 'mediamarkt' },
    { name: 'Lidl', url: 'https://www.lidl.de/c/aktuelle-angebote/a10006065', emoji: '🏪', tag: 'lidl' },
  ],
  ES: [
    { name: 'Amazon ES', url: 'https://www.amazon.es/gp/goldbox', emoji: '🛒', tag: 'amazon-es' },
    { name: 'Carrefour ES', url: 'https://www.carrefour.es/promociones', emoji: '🏪', tag: 'carrefour-es' },
    { name: 'El Corte Inglés', url: 'https://www.elcorteingles.es/promociones/', emoji: '🛍', tag: 'eci' },
    { name: 'PcComponentes', url: 'https://www.pccomponentes.com/ofertas', emoji: '🔌', tag: 'pccomp' },
  ],
  IT: [
    { name: 'Amazon IT', url: 'https://www.amazon.it/gp/goldbox', emoji: '🛒', tag: 'amazon-it' },
    { name: 'Esselunga', url: 'https://www.esselunga.it/cms/promozioni.html', emoji: '🏪', tag: 'esselunga' },
    { name: 'Unieuro', url: 'https://www.unieuro.it/online/promozioni', emoji: '🔌', tag: 'unieuro' },
  ],
  NL: [
    { name: 'Bol.com', url: 'https://www.bol.com/nl/nl/m/aanbiedingen/', emoji: '🛒', tag: 'bol' },
    { name: 'Coolblue', url: 'https://www.coolblue.nl/aanbiedingen', emoji: '🔌', tag: 'coolblue' },
    { name: 'Albert Heijn', url: 'https://www.ah.nl/bonus', emoji: '🏪', tag: 'ah' },
  ],
  CA: [
    { name: 'Amazon CA', url: 'https://www.amazon.ca/gp/goldbox', emoji: '🛒', tag: 'amazon-ca' },
    { name: 'Costco CA', url: 'https://www.costco.ca/online-only-online.html', emoji: '🏬', tag: 'costco-ca' },
    { name: 'Walmart CA', url: 'https://www.walmart.ca/en/savings-centre', emoji: '🏪', tag: 'walmart-ca' },
  ],
  AU: [
    { name: 'Amazon AU', url: 'https://www.amazon.com.au/gp/goldbox', emoji: '🛒', tag: 'amazon-au' },
    { name: 'JB Hi-Fi', url: 'https://www.jbhifi.com.au/pages/sale', emoji: '🔌', tag: 'jbhifi' },
    { name: 'Coles', url: 'https://www.coles.com.au/catalogues-and-specials', emoji: '🏪', tag: 'coles' },
  ],
  // Middle East & North Africa
  AE: [
    { name: 'Noon', url: 'https://www.noon.com/uae-en/sale-offers/', emoji: '🛒', tag: 'noon' },
    { name: 'Amazon AE', url: 'https://www.amazon.ae/gp/goldbox', emoji: '🛒', tag: 'amazon-ae' },
    { name: 'Carrefour UAE', url: 'https://www.carrefouruae.com/mafuae/en/c/promotions', emoji: '🏪', tag: 'carrefour-ae' },
    { name: 'Sharaf DG', url: 'https://uae.sharafdg.com/offers', emoji: '🔌', tag: 'sharaf' },
  ],
  SA: [
    { name: 'Noon SA', url: 'https://www.noon.com/saudi-en/sale-offers/', emoji: '🛒', tag: 'noon-sa' },
    { name: 'Amazon SA', url: 'https://www.amazon.sa/gp/goldbox', emoji: '🛒', tag: 'amazon-sa' },
    { name: 'Jarir', url: 'https://www.jarir.com/sa-en/discount/', emoji: '📚', tag: 'jarir' },
    { name: 'eXtra', url: 'https://www.extra.com/en-sa/c/promotions/N-1z140iiZ1z140i9', emoji: '🔌', tag: 'extra' },
  ],
  EG: [
    { name: 'Amazon EG', url: 'https://www.amazon.eg/gp/goldbox', emoji: '🛒', tag: 'amazon-eg' },
    { name: 'Noon EG', url: 'https://www.noon.com/egypt-en/sale-offers/', emoji: '🛒', tag: 'noon-eg' },
    { name: 'Jumia EG', url: 'https://www.jumia.com.eg/mlp-flash-sales/', emoji: '🛍', tag: 'jumia-eg' },
  ],
  MA: [
    { name: 'Jumia MA', url: 'https://www.jumia.ma/mlp-flash-sales/', emoji: '🛍', tag: 'jumia-ma' },
    { name: 'Marjane', url: 'https://www.marjane.ma/promotions', emoji: '🏪', tag: 'marjane' },
    { name: 'Decathlon', url: 'https://www.decathlon.ma/fr/landing-promo', emoji: '🏃', tag: 'decathlon-ma' },
    { name: 'Hmizate', url: 'https://www.hmizate.ma/', emoji: '🛍', tag: 'hmizate' },
  ],
  NG: [
    { name: 'Jumia NG', url: 'https://www.jumia.com.ng/mlp-flash-sales/', emoji: '🛍', tag: 'jumia-ng' },
    { name: 'Konga', url: 'https://www.konga.com/deals', emoji: '🛒', tag: 'konga' },
  ],
  IN: [
    { name: 'Amazon IN', url: 'https://www.amazon.in/gp/goldbox', emoji: '🛒', tag: 'amazon-in' },
    { name: 'Flipkart', url: 'https://www.flipkart.com/offers-list/top-offers', emoji: '🛒', tag: 'flipkart' },
    { name: 'Myntra', url: 'https://www.myntra.com/deals', emoji: '👕', tag: 'myntra' },
    { name: 'Big Basket', url: 'https://www.bigbasket.com/offers/all-offers/', emoji: '🥦', tag: 'bigbasket' },
  ],
  PK: [
    { name: 'Daraz', url: 'https://www.daraz.pk/wow', emoji: '🛒', tag: 'daraz' },
    { name: 'Telemart', url: 'https://www.telemart.pk/sale.html', emoji: '🔌', tag: 'telemart' },
  ],
  ID: [
    { name: 'Tokopedia', url: 'https://www.tokopedia.com/deals/today', emoji: '🛒', tag: 'tokopedia' },
    { name: 'Shopee ID', url: 'https://shopee.co.id/m/super-flash-sale', emoji: '🛍', tag: 'shopee-id' },
    { name: 'Blibli', url: 'https://www.blibli.com/promo', emoji: '🛍', tag: 'blibli' },
  ],
  // Default: global Amazon + AliExpress fallback (last resort)
  DEFAULT: [
    { name: 'Amazon', url: 'https://www.amazon.com/gp/goldbox', emoji: '🛒', tag: 'amazon' },
    { name: 'AliExpress', url: 'https://www.aliexpress.com/category/100000343/4.html', emoji: '🛍', tag: 'aliexpress' },
  ],
};

export const ShopStrip: Widget = {
  id: 'shop-strip',
  label: {
    en: 'Shop',
    fr: 'Acheter',
    ar: 'تسوق',
    es: 'Comprar',
    de: 'Einkaufen',
  },
  icon: '🛍',
  defaultVisible: true,
  defaultOrder: 28,
  defaultSize: 'compact',

  render(container: HTMLElement, size: 'compact' | 'expanded', ctx: WidgetContext): void {
    void size;
    const t = ctx.t;
    const loc = ctx.getLocationService();
    const cc = loc.countryCode || 'DEFAULT';
    const list = RETAILERS[cc] || RETAILERS.DEFAULT;

    container.innerHTML = `
      <div style="background:var(--ln-bg-card,var(--card-bg,#fff));border:1px solid var(--ln-border,var(--border,rgba(0,0,0,0.06)));border-radius:16px;padding:14px;">
        <div style="display:flex;align-items:center;gap:8px;margin-bottom:10px;">
          <span style="font-size:18px;">🛍</span>
          <span style="font-size:13px;font-weight:700;">${escapeHtml(t('widget_shop_title') || 'Today\'s deals')}</span>
          <span style="margin-inline-start:auto;font-size:11px;color:var(--ln-text-secondary,#6b6657);">${escapeHtml(cc === 'DEFAULT' ? '🌍' : cc)}</span>
        </div>

        <div style="display:grid;grid-template-columns:repeat(2,1fr);gap:6px;">
          ${list.slice(0, 4).map((r) => `
            <a href="${escapeAttr(r.url)}" target="_blank" rel="noopener noreferrer sponsored"
              style="display:flex;align-items:center;gap:8px;padding:10px;border-radius:10px;border:1px solid var(--ln-border,rgba(0,0,0,0.06));background:var(--ln-bg-card,#fff);text-decoration:none;color:inherit;min-height:48px;">
              <span style="font-size:18px;flex-shrink:0;">${r.emoji}</span>
              <span style="font-size:13px;font-weight:700;flex:1;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;color:var(--ln-text-primary,#1E293B);">${escapeHtml(r.name)}</span>
              <span style="font-size:11px;color:var(--ln-text-secondary,#6b6657);">→</span>
            </a>
          `).join('')}
        </div>

        <div style="margin-top:8px;font-size:10px;color:var(--ln-text-secondary,#6b6657);text-align:center;">${escapeHtml(t('widget_shop_disclaimer') || 'Curated retailers — opens in a new tab.')}</div>
      </div>
    `;
  },
};

function escapeHtml(s: string): string {
  return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
function escapeAttr(s: string): string {
  return String(s).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
}
